import React from "react";
import { X, Plus, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full border border-parchment rounded-md px-3 py-2.5 text-ink bg-white focus:outline-none focus:border-forest";

const EMPTY = {
  name: "", description: "", price: 0, category: "General",
  images: [], requires_shipping: true, stock: -1, variants: [], published: true, featured: false,
};

const parseVariants = (text) => (text || "").split("\n").map((l) => {
  const [name, opts] = l.split(":");
  if (!name || !opts) return null;
  return { name: name.trim(), options: opts.split(",").map((o) => o.trim()).filter(Boolean) };
}).filter((v) => v && v.options.length);

const variantsToText = (variants) => (variants || []).map((v) => `${v.name}: ${(v.options || []).join(", ")}`).join("\n");

// Inline product create/edit popup used on the public Shop page for admins.
export default function ProductEditorModal({ product, onClose, onSaved }) {
  const isNew = product === "new";
  const [form, setForm] = React.useState(isNew ? EMPTY : { ...EMPTY, ...product });
  const [variantsText, setVariantsText] = React.useState(isNew ? "" : variantsToText(product.variants));
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [uploadPct, setUploadPct] = React.useState(null);
  const [catOptions, setCatOptions] = React.useState([]);

  React.useEffect(() => {
    api.get("/shop/categories").then((r) => setCatOptions(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const uploadImage = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        setUploadPct(0);
        const { data } = await api.post("/admin/upload-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => { if (ev.total) setUploadPct(Math.min(99, Math.round((ev.loaded / ev.total) * 100))); },
        });
        const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setForm((f) => ({ ...f, images: [...f.images, url] }));
      }
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploading(false); setUploadPct(null); }
  };

  const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Product name is required."); return; }
    setSaving(true);
    const payload = {
      name: form.name, description: form.description, price: Number(form.price) || 0,
      category: form.category || "General", images: form.images,
      requires_shipping: form.requires_shipping, stock: Number(form.stock) ?? -1,
      variants: parseVariants(variantsText), published: form.published, featured: !!form.featured,
    };
    try {
      if (isNew) { await api.post("/admin/products", payload); toast.success("Product added."); }
      else { await api.put(`/admin/products/${product.id}`, payload); toast.success("Product updated."); }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="shop-product-editor">
      <div className="absolute inset-0 bg-night/60" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-serif text-2xl text-ink">{isNew ? "New Product" : "Edit Product"}</h3>
          <button onClick={onClose} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Price (USD)</label>
              <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Category</label>
              <input list="shop-cat-options" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Attire, Crafts, Art…" data-testid="product-category-input" className={inputCls} />
              <datalist id="shop-cat-options">
                {catOptions.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} data-testid="product-description-input" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Photos</label>
            <label className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
              {uploading ? "Uploading…" : "Upload photos"}
              <input type="file" accept="image/*" multiple onChange={uploadImage} disabled={uploading} data-testid="product-image-upload-input" className="hidden" />
            </label>
            {uploadPct !== null && (
              <div className="mt-3" data-testid="product-upload-progress">
                <div className="flex justify-between text-xs text-ink-muted mb-1"><span>Uploading…</span><span>{uploadPct}%</span></div>
                <div className="h-2 rounded-full bg-parchment overflow-hidden"><div className="h-full bg-gold transition-all duration-200" style={{ width: `${uploadPct}%` }} /></div>
              </div>
            )}
            {form.images.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {form.images.map((src, i) => (
                  <div key={i} className="relative">
                    <img src={src} alt="" className="h-24 w-full object-cover rounded-lg border border-parchment" />
                    <button type="button" onClick={() => removeImage(i)} data-testid={`product-image-remove-${i}`} className="absolute -top-2 -right-2 grid place-items-center w-6 h-6 rounded-full bg-night text-cream"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Options / variants <span className="text-ink-muted font-normal">(one per line, e.g. <code>Size: S, M, L</code>)</span></label>
            <textarea value={variantsText} onChange={(e) => setVariantsText(e.target.value)} rows={2} placeholder="Size: S, M, L&#10;Colour: Indigo, Gold" data-testid="product-variants-input" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Stock (−1 = unlimited)</label>
              <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock-input" className={inputCls} />
            </div>
            <label className="flex items-end gap-2 text-sm text-ink pb-2.5">
              <input type="checkbox" checked={form.requires_shipping} onChange={(e) => setForm({ ...form, requires_shipping: e.target.checked })} data-testid="product-shipping-checkbox" className="w-4 h-4 accent-forest" />
              Needs shipping (physical)
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="product-published-checkbox" className="w-4 h-4 accent-forest" />
              Published
            </label>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="product-featured-checkbox" className="w-4 h-4 accent-gold" />
              <Star className="w-4 h-4 text-gold" /> Feature (Shop + homepage)
            </label>
          </div>
          <button type="submit" disabled={saving} data-testid="save-product-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
            {saving ? "Saving..." : "Save Product"}
          </button>
        </form>
      </div>
    </div>
  );
}
